/**
 * The Sketch's 2D view: the satellite basemap, the drawn roof (lines, faces with their slope), the modules and the
 * ring gizmo, the shade sources (trees, roof obstacles, neighbouring buildings and walls, with their shadows at the
 * chosen sun time) and the solar-access heatmap, in local metres (north up). It owns pointer gestures and the
 * drawing feedback only (hover, snapping cues, lengths, the ghost module, the shade gizmo's radius and height
 * knobs); every change goes out through `onSketch` / `onPanels` / `onShade` / `onObstacles` (logic: skeleton.ts,
 * templates.ts, gizmo.ts, shade.ts). Styling: sketch.css (`.sk-canvas…` classes).
 */
import { Maximize2, Minus, Plus } from "lucide-react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { S } from "../api/client";
import { planToUv, pxToEnuTransform, type Vec2 } from "../editor/geo.ts";
import { panelGeometry } from "../editor/layout.ts";
import type { ObstacleItem, PanelItem } from "../editor/model.ts";
import type { SunPosition } from "../editor/layers/sun.ts";
import { faceAt, type LiveRoof } from "../editor/roof.ts";
import type { ConflictKind, ModuleSize, Orientation } from "../editor/snap.ts";
import { clampMpp, enuToScreen, frameView, screenToEnu, zoomAt, type View } from "../editor/viewport.ts";
import { constrain, gizmoFrame, magnetise, movePanels, placeAt, rehost, rotatePanels, snapAngle, turnAngle, type Handle, type PlaceContext, type PlaceMode } from "./gizmo.ts";
import { addLine, centroid, mainDirection, moveVertex, nextId, snapDirection, snapPoint, type EdgeType, type FaceSettings, type Sketch, type Slope, type Snap } from "./skeleton.ts";
import { apply as applyAffine, corners as pictureCorners, invert as invertAffine, moved as movedAffine, turnedScaled, type Affine } from "./align.ts";
import { newDormer, type DormerKind } from "./dormers.ts";
import { dormerShape, fitDormer } from "./dormerFit.ts";

export { dormerShape };
import { MAX_TYPED_M, measureLabel, parseTyped, smartSnap, typedPoint, typedTooLong, type SmartSnap } from "./snaps.ts";
import { buildingOf, centroid as ringCentre, crownBase, groundShadow, heatColor, moveObstacle, moveShade, obstacleBox, obstacleCircle, obstacleCylinder, obstacleHit, OBSTACLE_PRESETS, resizeCylinder, setHeight, setRadius, shadeAt, shapeOf, wallBetween, type ShadeItem } from "./shade.ts";
import type { NotePin, SketchSelection, SketchTool } from "./types.ts";
import { wheelAction, type Bindings } from "../editor/hotkeys.ts";
import type { MessageKey } from "../i18n/keys";
import { settle, WELD_M, type Gap } from "./gaps.ts";
import { pinchStart, pinchView, type Pinch } from "./touch.ts";
import { drawPanels, panelBatches, ringPath, type PanelLook, type ScreenPanel } from "./panelBatch.ts";
import { nextObstacle, nextTree, WHEEL_GESTURE, wheelSteps, type ShadeOptions, type WheelGesture } from "./variants.ts";
import { applyWheel, wheelTarget } from "./wheel.ts";
import { compassName, labelWidth, type Text } from "./text.ts";
import { useSketchText } from "./useText.ts";

export interface CanvasImage {
  url: string | null;
  width: number;
  height: number;
  enuToPx: number[];
}

interface Props {
  image: CanvasImage | null;
  attribution: string;
  sketch: Sketch;
  roof: LiveRoof;
  /** Where modules may go (setbacks); null: anywhere on the faces. */
  setbacks: S["SetbacksOutput"] | null;
  slopes: Record<string, Slope>;
  panels: PanelItem[];
  conflicts: Record<string, ConflictKind[]>;
  module: ModuleSize;
  gap: number;
  tool: SketchTool;
  lineType: EdgeType;
  placeMode: PlaceMode;
  orientation: Orientation;
  defaults: FaceSettings;
  selection: SketchSelection;
  readOnly: boolean;
  onSelect: (s: SketchSelection) => void;
  onSketch: (s: Sketch, transient?: boolean) => void;
  onPanels: (p: PanelItem[], transient?: boolean) => void;
  onCheckpoint: () => void;
  /** A footprint was closed: the corners, for the shape chooser. */
  onFootprint: (corners: Vec2[]) => void;
  /** The number of footprint corners clicked so far (the tool bar shows the steps). */
  onCorners?: (n: number) => void;
  /** Roof hints under review: Google's roof planes as dashed boxes (matched ones quieter). */
  hintBoxes?: { points: Vec2[]; label: string; matched: boolean }[];
  /** Faces a hint just changed (they flash once). */
  flashFaces?: ReadonlySet<string>;
  /** Roof obstacles (they block modules and cast shade) and the shade sources around the house. */
  obstacles: ObstacleItem[];
  shade: ShadeItem[];
  shadeOptions: ShadeOptions;
  onObstacles: (o: ObstacleItem[], transient?: boolean) => void;
  onShade: (s: ShadeItem[], transient?: boolean) => void;
  /** Solar access per module key (the heatmap); null: modules in their own look. */
  heat?: Map<string, number> | null;
  /** The sun the shadows are drawn for; null: no shadows. */
  sun?: SunPosition | null;
  /** Phase 4: faces of hidden structures (left out of the view), the structure new roofs go to, the dormer tool's
   * kind, and the footprint side the selected line lies on (its gable ↔ hip chip). */
  hiddenFaces?: ReadonlySet<string>;
  activeStructure?: string;
  dormerKind?: DormerKind;
  footprintSide?: { mode: "hip" | "gable"; allowed: boolean } | null;
  onToggleSide?: () => void;
  /** Placing an uploaded picture over the map (drag, turn, scale, or control points). */
  align?: AlignState | null;
  /** Phase 5: the installer's notes as numbered pins; a click opens one; the note tool pins a new one. */
  notes?: NotePin[];
  onNote?: (id: string) => void;
  onNoteAt?: (at: Vec2, faceId: string | null) => void;
  /** The wheel gestures (editor/hotkeys.ts) and what they change besides the selection: the next obstacle or tree,
   * the orientation, turning the selected panels. */
  bindings?: Bindings;
  mac?: boolean;
  onShadeOptions?: (o: Partial<ShadeOptions>) => void;
  onOrientation?: (o: Orientation) => void;
  onTurnPanels?: (deg: number) => void;
  /** Settle what a gesture drew (gaps.ts `settle`): corners join, lines split, small height slips close. */
  settleEdits?: boolean;
  /** The roof check: the gaps found, numbered, the selected one shown with where its fix goes. */
  gaps?: { list: Gap[]; selected: string | null; onSelect: (id: string | null) => void } | null;
}

export interface AlignState {
  url: string | null;
  width: number;
  height: number;
  m: Affine;
  mode: "drag" | "points";
  opacity: number;
  points: { image: Vec2; enu?: Vec2 }[];
  onChange: (m: Affine, transient?: boolean) => void;
  onPoint: (p: { image?: Vec2; enu?: Vec2 }) => void;
}

type Drag =
  | { kind: "pan"; start: Vec2; view: View }
  | { kind: "vertex"; id: string }
  | { kind: "gizmo"; handle: Handle; faceId: string; startUv: Vec2; centreUv: Vec2; panels: PanelItem[]; turned: number; startScreen: Vec2 }
  | { kind: "move-shade"; key: string; start: Vec2; items: ShadeItem[] }
  | { kind: "move-obstacle"; key: string; start: Vec2; items: ObstacleItem[] }
  | { kind: "knob"; knob: "radius" | "height"; key: string; target: "shade" | "obstacle"; startY: number; start: number }
  | { kind: "create"; tool: "tree" | "obstacle"; start: Vec2; now: Vec2 }
  | { kind: "move-dormer"; id: string; start: Vec2; centre: Vec2 }
  | { kind: "align"; handle: "move" | "turn" | "scale"; start: Vec2; m: Affine; pivot: Vec2 }
  | null;

type Hover = { kind: "vertex" | "edge" | "face" | "panel" | "shade" | "obstacle" | "dormer"; id: string } | { kind: "handle"; handle: Handle } | { kind: "knob"; knob: "radius" | "height" } | null;

/** Pixels per metre of the height knob's stem (it stays readable at every zoom). */
const STEM_PX_PER_M = 7;
const STEM_MIN_PX = 26;

const GIZMO_MIN_PX = 76;
/** Face tints: the brand's blues and violets first, then warm hues, all readable on shingles and on grass. */
export const FACE_HUE = ["#3b5bff", "#0ea5ff", "#8b5cf6", "#10b981", "#f59e0b", "#ec4899", "#14b8a6", "#f97316"];
const DEG = Math.PI / 180;
const ARC_DEG = 22; // half the turn handle's arc along the ring
/** Tools whose touch places something: done when the finger lifts (a second finger makes it a pinch instead). */
const TAP_TOOLS = new Set<SketchTool>(["line", "footprint", "panel", "note", "dormer", "neighbour"]);
const TAP_SLOP_PX = 12;

/** Key names (never translated). */
const KEY_ENTER = "Enter";

export function SketchCanvas(p: Props) {
  const tx = useSketchText();
  const txRef = useRef(tx);
  txRef.current = tx;
  const host = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const [view, setView] = useState<View>({ cx: 0, cy: 0, mpp: 0.05 });
  const [hover, setHover] = useState<Vec2 | null>(null);
  const [alt, setAlt] = useState(false);
  const [chain, setChain] = useState<Snap | null>(null);
  const [corners, setCorners] = useState<Vec2[]>([]);
  const [dragging, setDragging] = useState<Drag>(null);
  const [turn, setTurn] = useState<{ from: number; screenDelta: number; deg: number } | null>(null);
  const [snapped, setSnapped] = useState<Set<string>>(new Set());
  // modules just placed pop in (one path for all of them: a fill of a whole face pops as one block)
  const [fresh, setFresh] = useState<ReadonlySet<string>>(EMPTY_SET);
  const [freshTick, setFreshTick] = useState(0);
  const seenPanels = useRef<Set<string> | null>(null);
  useEffect(() => {
    const keys = new Set(p.panels.map((x) => x.key));
    const before = seenPanels.current;
    seenPanels.current = keys;
    if (!before || drag.current) return;
    const added = new Set([...keys].filter((k) => !before.has(k)));
    if (!added.size) return;
    setFresh(added);
    setFreshTick((t) => t + 1);
    const t = window.setTimeout(() => setFresh(EMPTY_SET), 420);
    return () => window.clearTimeout(t);
  }, [p.panels]);
  const [wallStart, setWallStart] = useState<Vec2 | null>(null);
  const [typed, setTypedState] = useState("");
  const typedRef = useRef("");
  const setTyped = (v: string) => {
    typedRef.current = v;
    setTypedState(v);
  };
  /** The pointer now (typed lengths read it at once, before React renders the last move). */
  const pointer = useRef<{ q: Vec2; alt: boolean } | null>(null);
  const drag = useRef<Drag>(null);
  const framed = useRef(false);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);
  const frame = () => {
    const pts = Object.values(p.sketch.vertices);
    const v = frameView(pts.length ? pts : [[-12, -12], [12, 12]], size.w, size.h, 4);
    if (v) setView(v);
  };
  useEffect(() => {
    if (framed.current || size.w < 10) return;
    frame();
    framed.current = true;
  }, [size, p.sketch.vertices]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setChain(null);
    setCorners([]);
    setWallStart(null);
    setTyped("");
  }, [p.tool, p.shadeOptions.neighbour]);
  useEffect(() => p.onCorners?.(corners.length), [corners.length]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!snapped.size) return;
    const t = window.setTimeout(() => setSnapped(new Set()), 500);
    return () => window.clearTimeout(t);
  }, [snapped]);

  const S = (q: Vec2) => enuToScreen(view, size.w, size.h, q);
  const at = (e: { clientX: number; clientY: number }): Vec2 => {
    const r = host.current!.getBoundingClientRect();
    return screenToEnu(view, size.w, size.h, e.clientX - r.left, e.clientY - r.top);
  };
  const tol = 10 * view.mpp; // ten screen pixels
  const ctx: PlaceContext = { roof: p.roof, panels: p.panels, obstacles: p.obstacles, setbacks: p.setbacks, module: p.module, gap: p.gap };
  const selectedKeys = useMemo(() => new Set(p.selection?.kind === "panels" ? p.selection.keys : []), [p.selection]);
  const framed0 = useMemo(() => gizmoFrame(p.panels, selectedKeys, p.roof, p.module), [p.panels, selectedKeys, p.roof, p.module]);
  // the ring keeps a usable size on screen (handles ≥ 44 px apart) however far the view is zoomed out
  const gizmo = framed0 ? { ...framed0, radius: Math.max(framed0.radius, GIZMO_MIN_PX * view.mpp) } : null;
  const showGizmo = !!gizmo && p.tool === "select" && !p.readOnly;
  const geometry = useMemo(() => new Map(p.panels.map((x) => [x.key, panelGeometry(x, p.roof, p.module).plan])), [p.panels, p.roof, p.module]);
  const houseDeg = mainDirection(p.sketch);
  // dormers: their faces (from the live roof model, as the server makes them) and boxes
  const dormerShapes = useMemo(() => (p.sketch.dormers ?? []).map((d) => dormerShape(p.roof, d)), [p.sketch.dormers, p.roof]);
  const dormerAt = (q: Vec2): string | null => {
    for (const d of [...dormerShapes].reverse()) if (d.box.length && inside(q, d.box)) return d.id;
    return null;
  };
  const hidden = p.hiddenFaces ?? EMPTY_SET;
  const visibleVertices = useMemo(() => {
    if (!hidden.size) return null;
    const keep = new Set<string>();
    const inFace = new Set<string>();
    for (const f of p.sketch.faces) for (const v of f.boundary) (hidden.has(f.id) ? inFace : keep).add(v);
    for (const v of Object.keys(p.sketch.vertices)) if (!inFace.has(v)) keep.add(v);
    return keep;
  }, [hidden, p.sketch]);
  const lineVisible = (a: string, b: string) => !visibleVertices || visibleVertices.has(a) || visibleVertices.has(b);
  // drawing: where the next point lands (snaps and guides) and the typed length
  const prevDeg = (() => {
    if (p.tool === "footprint" && corners.length >= 2) {
      const a = corners[corners.length - 2]!, b = corners[corners.length - 1]!;
      return Math.atan2(b[1] - a[1], b[0] - a[0]) / DEG;
    }
    return null;
  })();
  const anchor: Vec2 | null = p.tool === "line" ? chain?.point ?? null : p.tool === "footprint" ? corners[corners.length - 1] ?? null : null;
  const segments = p.tool === "footprint" ? corners.slice(1).map((q, i) => [corners[i]!, q] as [Vec2, Vec2]) : [];
  const snapFor = (q: Vec2, free: boolean): SmartSnap => smartSnap({ sketch: p.sketch, pointer: q, anchor, prevDeg, tol, points: p.tool === "footprint" ? corners : [], segments, free });
  const typedValue = parseTyped(typed);
  // the selected shade source or obstacle, and its gizmo's knobs (radius on its east side, height on a stem)
  const sel = p.selection;
  const selShade = sel?.kind === "shade" ? p.shade.find((x) => x.key === sel.key) ?? null : null;
  const selObstacle = sel?.kind === "obstacle" ? p.obstacles.find((x) => x.key === sel.key) ?? null : null;
  const knobs = (() => {
    if (p.readOnly || p.tool !== "select") return null;
    if (selShade) {
      const anchor = selShade.kind === "tree" ? selShade.center! : ringCentre(selShade.polygon!);
      return { anchor, radius: selShade.kind === "tree" ? selShade.radius ?? 0 : null, height: selShade.height };
    }
    if (selObstacle) {
      const c = obstacleCircle(selObstacle);
      return { anchor: c.center, radius: shapeOf(selObstacle) === "cylinder" ? c.radius : null, height: selObstacle.height_m ?? 0 };
    }
    return null;
  })();
  const knobAt = (q: Vec2): "radius" | "height" | null => {
    if (!knobs) return null;
    const a = S(knobs.anchor);
    const at = S(q);
    if (knobs.radius != null) {
      const r = S([knobs.anchor[0] + knobs.radius, knobs.anchor[1]]);
      if (Math.hypot(at[0] - r[0], at[1] - r[1]) <= 11) return "radius";
    }
    const top = a[1] - stemPx(knobs.height);
    if (Math.hypot(at[0] - a[0], at[1] - top) <= 12) return "height";
    return null;
  };

  // ------------------------------------------------------------------ hit tests
  const panelAt = (q: Vec2): string | null => {
    for (const panel of [...p.panels].reverse()) {
      const plan = geometry.get(panel.key) ?? [];
      if (plan.length && inside(q, plan)) return panel.key;
    }
    return null;
  };
  const gizmoHandleAt = (q: Vec2): Handle | null => {
    if (!gizmo || !showGizmo) return null;
    const c = gizmo.centrePlan;
    for (const h of handles(gizmo)) if (h.handle !== "rotate" && Math.hypot(q[0] - h.at[0], q[1] - h.at[1]) <= Math.max(tol * 1.5, 0.25)) return h.handle;
    // the turn handle is an arc along the ring
    const rot = handles(gizmo).find((h) => h.handle === "rotate")!;
    const r = Math.hypot(q[0] - c[0], q[1] - c[1]);
    const da = Math.abs(angleDiff(Math.atan2(q[1] - c[1], q[0] - c[0]), Math.atan2(rot.at[1] - c[1], rot.at[0] - c[0])));
    if (Math.abs(r - gizmo.radius) <= Math.max(tol * 1.4, 0.25) && da <= (ARC_DEG + 6) * DEG) return "rotate";
    if (Math.hypot(q[0] - c[0], q[1] - c[1]) <= Math.max(tol * 1.8, 0.3)) return "centre";
    return null;
  };

  // ------------------------------------------------------------------ gestures
  /** A drawing click (or a typed length): the next line end or footprint corner. */
  const commitPoint = (point: Vec2, snap?: SmartSnap) => {
    if (p.tool === "line") {
      const at: Snap = snap?.vertex ? { vertex: snap.vertex, point } : snap?.edge ? { edge: snap.edge, point } : snapPoint(p.sketch, point, 1e-6).vertex ? snapPoint(p.sketch, point, 1e-6) : { point };
      if (!chain) return setChain(at);
      let next = addLine(p.sketch, chain, at, p.lineType, p.defaults);
      if (p.settleEdits && next !== p.sketch) {
        // the corners this line made or reached: join near corners, split lines, settle small height slips
        const touched = [snapPoint(next, chain.point, 1e-6).vertex, snapPoint(next, at.point, 1e-6).vertex].filter((v): v is string => !!v);
        next = settle(next, touched, p.defaults, WELD_M);
      }
      p.onSketch(next);
      const end = snapPoint(next, at.point, WELD_M);
      setChain(end.vertex ? { vertex: end.vertex, point: end.point } : null);
      return;
    }
    if (p.tool === "footprint") {
      if (corners.length >= 3 && Math.hypot(point[0] - corners[0]![0], point[1] - corners[0]![1]) <= tol * 1.5) {
        p.onFootprint(corners);
        setCorners([]);
        return;
      }
      setCorners([...corners, point]);
    }
  };
  /** Placing an uploaded picture: drag it, its turn knob or a corner square; or a control point. */
  const alignDown = (q: Vec2, e: { clientX: number; clientY: number; pointerId: number }) => {
    const a = p.align!;
    if (a.mode === "points") {
      const last = a.points[a.points.length - 1];
      if (last && !last.enu) a.onPoint({ enu: q });
      else if (a.points.length < 3) a.onPoint({ image: applyAffine(invertAffine(a.m), q) });
      return;
    }
    const box = pictureCorners(a.m, a.width, a.height);
    const centre: Vec2 = [(box[0]![0] + box[2]![0]) / 2, (box[0]![1] + box[2]![1]) / 2];
    const knob = turnKnob(box);
    if (Math.hypot(q[0] - knob[0], q[1] - knob[1]) <= tol * 1.6) return startDrag({ kind: "align", handle: "turn", start: q, m: a.m, pivot: centre }, e);
    for (const c of box) if (Math.hypot(q[0] - c[0], q[1] - c[1]) <= tol * 1.6) return startDrag({ kind: "align", handle: "scale", start: q, m: a.m, pivot: centre }, e);
    if (inside(q, box)) return startDrag({ kind: "align", handle: "move", start: q, m: a.m, pivot: centre }, e);
    startDrag({ kind: "pan", start: [e.clientX, e.clientY], view }, e);
  };
  const startDrag = (d: Drag, e: { pointerId: number }) => {
    drag.current = d;
    setDragging(d);
    host.current?.setPointerCapture(e.pointerId);
  };
  // touch (phase 6, touch.ts): two fingers pinch and pan the map; with a placing tool a finger places on lifting,
  // so a pinch never leaves a stray panel or corner where the first finger landed
  const touches = useRef(new Map<number, [number, number]>());
  const pinch = useRef<Pinch | null>(null);
  const tap = useRef<{ id: number; x: number; y: number; alt: boolean } | null>(null);
  const local = (e: { clientX: number; clientY: number }): [number, number] => {
    const r = host.current!.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };
  const down = (e: ReactPointerEvent) => {
    if (e.pointerType === "touch") {
      touches.current.set(e.pointerId, local(e));
      if (touches.current.size === 2) {
        // a second finger: what the first began ends where it is, and the fingers zoom and pan the map
        if (drag.current && drag.current.kind !== "pan") p.onCheckpoint();
        drag.current = null;
        setDragging(null);
        setTurn(null);
        tap.current = null;
        const [a, b] = [...touches.current.values()] as [[number, number], [number, number]];
        pinch.current = pinchStart(view, a, b);
        host.current?.setPointerCapture(e.pointerId);
        return;
      }
      if (touches.current.size > 2 || pinch.current) return;
      if (TAP_TOOLS.has(p.tool) && !p.align && !p.readOnly) {
        tap.current = { id: e.pointerId, x: e.clientX, y: e.clientY, alt: e.altKey };
        setHover(at(e));
        return;
      }
    }
    press(e);
  };
  const press = (e: { clientX: number; clientY: number; altKey: boolean; shiftKey: boolean; button: number; pointerId: number }) => {
    if (e.button === 1 || p.tool === "pan" || (e.altKey && e.shiftKey)) {
      startDrag({ kind: "pan", start: [e.clientX, e.clientY], view }, e);
      return;
    }
    const q = at(e);
    if (p.readOnly) return;
    if (p.align) {
      alignDown(q, e);
      return;
    }
    if (p.tool === "line" || p.tool === "footprint") {
      const value = typedValue;
      if (value && anchor) {
        commitPoint(typedPoint(anchor, value, snapFor(q, e.altKey).point, prevDeg));
        setTyped("");
        return;
      }
      const snap = snapFor(q, e.altKey);
      commitPoint(snap.point, snap);
      return;
    }
    if (p.tool === "note") {
      p.onNoteAt?.(q, faceAt(p.roof, q)?.id ?? null);
      return;
    }
    if (p.tool === "dormer") {
      const face = faceAt(p.roof, q);
      const drawn = face && p.sketch.faces.find((f) => f.id === face.id);
      if (!face || !drawn || face.pitch < 5) return;
      // a click near an edge would leave the dormer outside its face (and block the numbers and the save): it is
      // moved inwards until it fits, or not placed, with a word by the pointer
      const d = fitDormer(p.roof, newDormer(nextId("d", (p.sketch.dormers ?? []).map((x) => x.id)), face.id, p.dormerKind ?? "gable", q));
      if (!d) {
        const r = host.current?.getBoundingClientRect();
        if (r) setReadout((x) => ({ x: e.clientX - r.left, y: e.clientY - r.top, text: tx.t("sketch.canvas.noRoomDormer"), n: (x?.n ?? 0) + 1 }));
        if (readoutTimer.current) window.clearTimeout(readoutTimer.current);
        readoutTimer.current = window.setTimeout(() => setReadout(null), 2200);
        return;
      }
      p.onSketch({ ...p.sketch, dormers: [...(p.sketch.dormers ?? []), d] });
      p.onSelect({ kind: "dormer", id: d.id });
      return;
    }
    if (p.tool === "panel") {
      const placed = placeAt(ctx, q, p.orientation, p.placeMode);
      if (placed) p.onPanels([...p.panels, placed]);
      return;
    }
    if (p.tool === "tree" || p.tool === "obstacle") {
      startDrag({ kind: "create", tool: p.tool, start: q, now: q }, e);
      return;
    }
    if (p.tool === "neighbour") {
      if (p.shadeOptions.neighbour === "wall") {
        if (!wallStart) return setWallStart(q);
        const end = snapDirection(wallStart, q, 45, e.altKey ? 0 : 6, houseDeg);
        const wall = wallBetween(wallStart, end, p.shadeOptions.neighbourHeight ?? undefined);
        setWallStart(null);
        if (wall) {
          p.onShade([...p.shade, wall]);
          p.onSelect({ kind: "shade", key: wall.key });
        }
        return;
      }
      const corner = footprintCorner(corners, q, e.altKey, houseDeg);
      if (corners.length >= 3 && Math.hypot(corner[0] - corners[0]![0], corner[1] - corners[0]![1]) <= tol * 1.5) {
        const building = buildingOf(corners, p.shadeOptions.neighbourHeight ?? undefined);
        p.onShade([...p.shade, building]);
        p.onSelect({ kind: "shade", key: building.key });
        setCorners([]);
        return;
      }
      setCorners([...corners, corner]);
      return;
    }
    // select
    const knob = knobAt(q);
    if (knob && knobs && p.selection && (p.selection.kind === "shade" || p.selection.kind === "obstacle")) {
      startDrag({ kind: "knob", knob, key: p.selection.key, target: p.selection.kind, startY: e.clientY, start: knob === "radius" ? knobs.radius ?? 0 : knobs.height }, e);
      return;
    }
    const handle = gizmoHandleAt(q);
    if (handle && gizmo) {
      startDrag({ kind: "gizmo", handle, faceId: gizmo.face.id, startUv: planToUv(gizmo.face.frame, gizmo.face.normal, gizmo.face.d, q), centreUv: gizmo.centreUv, panels: p.panels, turned: 0, startScreen: [e.clientX, e.clientY] }, e);
      return;
    }
    const dormer = dormerAt(q);
    if (dormer) {
      p.onSelect({ kind: "dormer", id: dormer });
      const d = p.sketch.dormers!.find((x) => x.id === dormer)!;
      startDrag({ kind: "move-dormer", id: dormer, start: q, centre: d.center }, e);
      return;
    }
    const vertex = snapPoint(p.sketch, q, tol);
    if (vertex.vertex) {
      p.onSelect({ kind: "vertex", id: vertex.vertex });
      startDrag({ kind: "vertex", id: vertex.vertex }, e);
      return;
    }
    const key = panelAt(q);
    if (key) {
      const keys = e.shiftKey ? [...selectedKeys, key] : selectedKeys.has(key) ? [...selectedKeys] : [key];
      p.onSelect({ kind: "panels", keys });
      const face = p.roof.faces.find((f) => f.id === p.panels.find((x) => x.key === key)!.face_id)!;
      startDrag({ kind: "gizmo", handle: "centre", faceId: face.id, startUv: planToUv(face.frame, face.normal, face.d, q), centreUv: [0, 0], panels: p.panels, turned: 0, startScreen: [e.clientX, e.clientY] }, e);
      return;
    }
    const obstacle = obstacleHit(p.obstacles, q, tol * 0.5);
    if (obstacle) {
      p.onSelect({ kind: "obstacle", key: obstacle });
      startDrag({ kind: "move-obstacle", key: obstacle, start: q, items: p.obstacles }, e);
      return;
    }
    if (vertex.edge) return p.onSelect({ kind: "edge", id: vertex.edge });
    const face = faceAt(p.roof, q);
    const shadeKey = shadeAt(p.shade, q, tol * 0.5);
    if (shadeKey && !face) {
      p.onSelect({ kind: "shade", key: shadeKey });
      startDrag({ kind: "move-shade", key: shadeKey, start: q, items: p.shade }, e);
      return;
    }
    p.onSelect(face ? { kind: "face", id: face.id } : shadeKey ? { kind: "shade", key: shadeKey } : null);
  };

  const move = (e: ReactPointerEvent) => {
    if (e.pointerType === "touch" && touches.current.has(e.pointerId)) {
      touches.current.set(e.pointerId, local(e));
      if (pinch.current) {
        const pts = [...touches.current.values()];
        if (pts.length >= 2) setView(pinchView(pinch.current, pts[0]!, pts[1]!, size.w, size.h));
        return;
      }
      if (tap.current) {
        if (Math.hypot(e.clientX - tap.current.x, e.clientY - tap.current.y) > TAP_SLOP_PX) tap.current = null;
        setHover(at(e));
        return;
      }
    }
    const q = at(e);
    pointer.current = { q, alt: e.altKey };
    setHover(q);
    if (e.altKey !== alt) setAlt(e.altKey);
    const d = drag.current;
    if (!d) return;
    if (d.kind === "pan") {
      setView({ ...d.view, cx: d.view.cx - (e.clientX - d.start[0]) * d.view.mpp, cy: d.view.cy + (e.clientY - d.start[1]) * d.view.mpp });
      return;
    }
    if (d.kind === "vertex") {
      // a corner dragged near another (or onto a line) snaps there; dropped, it joins it (settle)
      const snap = snapPoint({ ...p.sketch, vertices: Object.fromEntries(Object.entries(p.sketch.vertices).filter(([id]) => id !== d.id)), edges: p.sketch.edges.filter((x) => x.a !== d.id && x.b !== d.id) }, q, Math.max(tol, e.altKey ? 0 : WELD_M * 0.6));
      p.onSketch(moveVertex(p.sketch, d.id, e.altKey ? q : snap.point), true);
      return;
    }
    if (d.kind === "create") {
      drag.current = { ...d, now: q };
      setDragging(drag.current);
      return;
    }
    if (d.kind === "move-dormer") {
      const centre: Vec2 = [d.centre[0] + q[0] - d.start[0], d.centre[1] + q[1] - d.start[1]];
      p.onSketch({ ...p.sketch, dormers: (p.sketch.dormers ?? []).map((x) => (x.id === d.id ? { ...x, center: centre } : x)) }, true);
      return;
    }
    if (d.kind === "align") {
      if (!p.align) return;
      if (d.handle === "move") p.align.onChange(movedAffine(d.m, [q[0] - d.start[0], q[1] - d.start[1]]), true);
      else {
        const a0 = Math.atan2(d.start[1] - d.pivot[1], d.start[0] - d.pivot[0]);
        const a1 = Math.atan2(q[1] - d.pivot[1], q[0] - d.pivot[0]);
        const r0 = Math.hypot(d.start[0] - d.pivot[0], d.start[1] - d.pivot[1]) || 1;
        const r1 = Math.hypot(q[0] - d.pivot[0], q[1] - d.pivot[1]);
        if (d.handle === "turn") p.align.onChange(turnedScaled(d.m, d.pivot, (a1 - a0) / DEG, 1), true);
        else p.align.onChange(turnedScaled(d.m, d.pivot, 0, Math.max(0.05, r1 / r0)), true);
      }
      return;
    }
    if (d.kind === "move-shade") {
      const delta: Vec2 = [q[0] - d.start[0], q[1] - d.start[1]];
      p.onShade(d.items.map((x) => (x.key === d.key ? moveShade(x, delta) : x)), true);
      return;
    }
    if (d.kind === "move-obstacle") {
      const delta: Vec2 = [q[0] - d.start[0], q[1] - d.start[1]];
      p.onObstacles(d.items.map((x) => (x.key === d.key ? moveObstacle(x, delta) : x)), true);
      return;
    }
    if (d.kind === "knob") {
      const value = d.knob === "height" ? d.start + (d.startY - e.clientY) / STEM_PX_PER_M : null;
      if (d.target === "shade") {
        p.onShade(p.shade.map((x) => {
          if (x.key !== d.key) return x;
          if (d.knob === "height") return setHeight(x, Math.round(value! * 2) / 2);
          const c = x.center!;
          return setRadius(x, Math.round(Math.hypot(q[0] - c[0], q[1] - c[1]) * 10) / 10);
        }), true);
      } else {
        p.onObstacles(p.obstacles.map((x) => {
          if (x.key !== d.key) return x;
          if (d.knob === "height") return { ...x, height_m: Math.max(0, Math.min(10, Math.round(value! * 10) / 10)) };
          const c = obstacleCircle(x).center;
          return resizeCylinder(x, Math.hypot(q[0] - c[0], q[1] - c[1]));
        }), true);
      }
      return;
    }
    // the gizmo: move on the plane, or turn about the selection's centre
    const keys = new Set(p.selection?.kind === "panels" ? p.selection.keys : []);
    const face = p.roof.faces.find((f) => f.id === d.faceId)!;
    let base = d.panels;
    if (d.handle === "rotate") {
      const now = planToUv(face.frame, face.normal, face.d, q);
      const angle = snapAngle(turnAngle(d.centreUv, d.startUv, now), 15, e.altKey);
      p.onPanels(rotatePanels(base, keys, face, d.centreUv, angle), true);
      if (gizmo) {
        const r = host.current!.getBoundingClientRect();
        const c = S(gizmo.centrePlan);
        const a0 = Math.atan2(d.startScreen[1] - r.top - c[1], d.startScreen[0] - r.left - c[0]);
        const a1 = Math.atan2(e.clientY - r.top - c[1], e.clientX - r.left - c[0]);
        const raw = angleDiff(a1, a0);
        setTurn({ from: a0, screenDelta: Math.sign(raw || 1) * Math.abs(angle) * DEG, deg: angle });
      }
      return;
    }
    const over = faceAt(p.roof, q);
    if (d.handle === "centre" && over && over.id !== face.id) {
      // crossing onto another face: re-host there, then keep dragging on its plane
      base = rehost(p.panels, keys, p.roof, over);
      drag.current = { ...d, faceId: over.id, panels: base, startUv: planToUv(over.frame, over.normal, over.d, q) };
      p.onPanels(base, true);
      return;
    }
    const now = planToUv(face.frame, face.normal, face.d, q);
    const delta = constrain(d.handle, [now[0] - d.startUv[0], now[1] - d.startUv[1]]);
    p.onPanels(movePanels(base, keys, p.roof, face, delta), true);
  };

  const up = (e?: ReactPointerEvent) => {
    if (e?.pointerType === "touch") {
      touches.current.delete(e.pointerId);
      if (pinch.current) {
        if (!touches.current.size) pinch.current = null; // the last finger lifted: the tools take touches again
        return;
      }
      const t = tap.current;
      tap.current = null;
      if (t && t.id === e.pointerId && e.type === "pointerup") press({ clientX: t.x, clientY: t.y, altKey: t.alt, shiftKey: false, button: 0, pointerId: e.pointerId });
      if (t) return;
    }
    const d = drag.current;
    drag.current = null;
    setDragging(null);
    setTurn(null);
    if (!d || d.kind === "pan") return;
    if (d.kind === "align") {
      p.align?.onChange(p.align.m);
      return;
    }
    if (d.kind === "create") {
      const size = Math.hypot(d.now[0] - d.start[0], d.now[1] - d.start[1]);
      if (d.tool === "tree") {
        const tree = nextTree(p.shadeOptions, d.start, size >= 0.4 ? Math.round(size * 10) / 10 : undefined);
        p.onShade([...p.shade, tree]);
        p.onSelect({ kind: "shade", key: tree.key });
      } else {
        const kind = p.shadeOptions.obstacle;
        const dragged = p.shadeOptions.shape === "cylinder" ? obstacleCylinder(kind, d.start, size) : obstacleBox(kind, d.start, d.now, houseDeg);
        const made = size < 0.15 ? nextObstacle(p.shadeOptions, d.start, houseDeg) : p.shadeOptions.obstacleHeight != null ? { ...dragged, height_m: p.shadeOptions.obstacleHeight } : dragged;
        p.onObstacles([...p.obstacles, made]);
        p.onSelect({ kind: "obstacle", key: made.key });
      }
      return;
    }
    if (d.kind === "vertex" && p.settleEdits) {
      const settled = settle(p.sketch, [d.id], p.defaults, Math.max(WELD_M, tol));
      if (settled !== p.sketch) {
        p.onSketch(settled, true);
        if (!settled.vertices[d.id]) p.onSelect(null);
      }
    }
    if (d.kind === "gizmo" && p.placeMode === "magnetic" && p.selection?.kind === "panels") {
      p.onPanels(magnetise(ctx, new Set(p.selection.keys)), true);
      setSnapped(new Set(p.selection.keys));
    }
    p.onCheckpoint();
  };

  // the wheel zooms; with a modifier (editor/hotkeys.ts) it changes the selected or the next obstacle or tree (size,
  // kind, height) or the panels (turn the selection, orientation), with a readout by the pointer. Listened to
  // natively (not passive) so the browser's own Shift/Alt scrolling is prevented over the map only.
  const wheelAcc = useRef(0);
  const wheelIdle = useRef<number | null>(null);
  const [readoutAt, setReadout] = useState<{ x: number; y: number; text: string; n: number } | null>(null);
  const readoutTimer = useRef<number | null>(null);
  // a copy of the latest props (React's are frozen): quick wheel events build on what the last one changed
  const live = useRef<Props>({ ...p });
  live.current = { ...p };
  const viewRef = useRef({ view, size });
  viewRef.current = { view, size };
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      const q = live.current;
      const r = el.getBoundingClientRect();
      const action = q.bindings ? wheelAction(e, q.bindings, !!q.mac) : null;
      const gesture: WheelGesture | undefined = action ? WHEEL_GESTURE[action] : undefined;
      if (gesture && !q.readOnly && wheelTarget(q, gesture)) {
        e.preventDefault();
        const { steps, acc } = wheelSteps(wheelAcc.current, e.deltaX, e.deltaY, e.deltaMode);
        wheelAcc.current = acc;
        if (!steps) return;
        const text = applyWheel(q, gesture, steps, txRef.current);
        if (text) {
          setReadout((x) => ({ x: e.clientX - r.left, y: e.clientY - r.top, text, n: (x?.n ?? 0) + 1 }));
          if (readoutTimer.current) window.clearTimeout(readoutTimer.current);
          readoutTimer.current = window.setTimeout(() => setReadout(null), 1100);
        }
        // a burst of wheel steps is one undo step
        if (wheelIdle.current) window.clearTimeout(wheelIdle.current);
        wheelIdle.current = window.setTimeout(() => live.current.onCheckpoint(), 450);
        return;
      }
      // the wheel alone, a trackpad pinch (Ctrl + wheel) or a gesture with nothing to change: zoom the map, not the page
      e.preventDefault();
      const { view: v0, size: sz } = viewRef.current;
      const at = screenToEnu(v0, sz.w, sz.h, e.clientX - r.left, e.clientY - r.top);
      setView((v) => zoomAt(v, at, Math.exp(e.deltaY * 0.0015)));
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("wheel", onWheel);
      if (wheelIdle.current) window.clearTimeout(wheelIdle.current);
      if (readoutTimer.current) window.clearTimeout(readoutTimer.current);
    };
  }, []);

  // typing a length (and angle) while drawing: digits go here, not to the editor's view keys
  useEffect(() => {
    const drawing = (p.tool === "line" && !!chain) || (p.tool === "footprint" && corners.length > 0);
    if (!drawing) return;
    const key = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) return;
      if (e.metaKey || e.ctrlKey) return;
      const typed = typedRef.current;
      if (/^[0-9.,<]$/.test(e.key) || (e.key === " " && typed)) {
        setTyped((typed + (e.key === "," ? "." : e.key)).slice(0, 16));
      } else if (e.key === "Backspace" && typed) {
        setTyped(typed.slice(0, -1));
      } else if (e.key === "Enter" && typed && typedTooLong(typed)) {
        // longer than a side may be: nothing drawn, the readout says why and the length stays to correct
      } else if (e.key === "Enter" && typed) {
        const value = parseTyped(typed);
        const now = pointer.current;
        if (value && anchor) commitPoint(typedPoint(anchor, value, now ? snapFor(now.q, now.alt).point : anchor, prevDeg));
        setTyped("");
      } else if (e.key === "Escape" && typed) {
        setTyped("");
      } else return;
      e.preventDefault();
      e.stopImmediatePropagation();
    };
    window.addEventListener("keydown", key, true);
    return () => window.removeEventListener("keydown", key, true);
  });

  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setChain(null);
        setCorners([]);
        setWallStart(null);
      }
      if (e.key === "Enter" && p.tool === "footprint" && corners.length >= 3) {
        // this Enter must not also press the shape chooser's first button, which takes the focus as it opens
        e.preventDefault();
        p.onFootprint(corners);
        setCorners([]);
      }
      if (e.key === "Enter" && p.tool === "neighbour" && corners.length >= 3) {
        e.preventDefault();
        const building = buildingOf(corners, p.shadeOptions.neighbourHeight ?? undefined);
        p.onShade([...p.shade, building]);
        p.onSelect({ kind: "shade", key: building.key });
        setCorners([]);
      }
    };
    window.addEventListener("keydown", key);
    return () => window.removeEventListener("keydown", key);
  }, [p, corners]);

  // ------------------------------------------------------------------ what the pointer is over (feedback only)
  const hovered: Hover = useMemo(() => {
    if (!hover || dragging || p.tool !== "select") return null;
    const knob = knobAt(hover);
    if (knob) return { kind: "knob", knob };
    const handle = gizmoHandleAt(hover);
    if (handle) return { kind: "handle", handle };
    const dormer = dormerAt(hover);
    if (dormer) return { kind: "dormer", id: dormer };
    const snap = snapPoint(p.sketch, hover, tol);
    if (snap.vertex) return { kind: "vertex", id: snap.vertex };
    const key = panelAt(hover);
    if (key) return { kind: "panel", id: key };
    const obstacle = obstacleHit(p.obstacles, hover, tol * 0.5);
    if (obstacle) return { kind: "obstacle", id: obstacle };
    if (snap.edge) return { kind: "edge", id: snap.edge };
    const face = faceAt(p.roof, hover);
    const shadeKey = shadeAt(p.shade, hover, tol * 0.5);
    if (shadeKey && !face) return { kind: "shade", id: shadeKey };
    return face ? { kind: "face", id: face.id } : null;
  }, [hover, dragging, p.tool, p.sketch, p.roof, p.shade, p.obstacles, knobs?.height, knobs?.radius, geometry, gizmo?.radius, gizmo?.centrePlan[0], gizmo?.centrePlan[1], view.mpp]); // eslint-disable-line react-hooks/exhaustive-deps

  // the module a click would place (panel tool): the same function as the click, so the ghost never lies
  const ghost = useMemo(() => {
    if (p.tool !== "panel" || !hover || p.readOnly) return null;
    const placed = placeAt(ctx, hover, p.orientation, p.placeMode);
    return placed ? panelGeometry(placed, p.roof, p.module).plan : null;
  }, [p.tool, hover, p.panels, p.roof, p.orientation, p.placeMode, p.setbacks, p.module]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------ drawing
  const imageMatrix = useMemo(() => {
    if (!p.image) return null;
    const [A, B, C, D, E, F] = pxToEnuTransform(p.image.enuToPx) as [number, number, number, number, number, number];
    const k = 1 / view.mpp;
    return `matrix(${A * k} ${-D * k} ${B * k} ${-E * k} ${(C - view.cx) * k + size.w / 2} ${-(F - view.cy) * k + size.h / 2})`;
  }, [p.image, view, size]);
  const path = (pts: Vec2[], close = true) => pts.map((q, i) => `${i ? "L" : "M"}${S(q)[0].toFixed(1)},${S(q)[1].toFixed(1)}`).join("") + (close ? "Z" : "");

  // line and footprint tools: exactly where a click (or the typed length) lands, with its guides and cue
  const drawSnap = (p.tool === "line" || p.tool === "footprint") && hover && !p.readOnly && !p.align ? (() => {
    const snap = snapFor(hover, alt);
    if (typedValue && anchor) {
      const point = typedPoint(anchor, typedValue, snap.point, prevDeg);
      return { ...snap, point, kind: "typed" as const, label: tx.t("sketch.unit.m", { value: typed }), guides: [], length: typedValue.length, turn: typedValue.angle };
    }
    return snap;
  })() : null;
  const lineCursor = p.tool === "line" && drawSnap ? { snap: { point: drawSnap.point, vertex: drawSnap.vertex, edge: drawSnap.edge } as Snap, squared: false } : null;
  // footprint (or a neighbouring building): the corner a click would add
  const footCursor = p.tool === "footprint" && drawSnap ? drawSnap.point : p.tool === "neighbour" && p.shadeOptions.neighbour === "building" && hover && !p.readOnly ? footprintCorner(corners, hover, alt, mainDirection(p.sketch)) : null;
  const closing = !!footCursor && corners.length >= 3 && Math.hypot(footCursor[0] - corners[0]![0], footCursor[1] - corners[0]![1]) <= tol * 1.5;
  const footSquared = p.tool === "neighbour" && !!footCursor && corners.length > 0 && !alt && hover && (footCursor[0] !== hover[0] || footCursor[1] !== hover[1]);

  const hoverFace = hovered?.kind === "face" ? hovered.id : null;
  const hoverEdge = hovered?.kind === "edge" ? hovered.id : null;
  const hoverVertex = hovered?.kind === "vertex" ? hovered.id : null;
  const hoverPanel = hovered?.kind === "panel" ? hovered.id : null;
  // the modules: a few batched paths (panelBatch.ts), made again only when they, the view or their states change
  const screenPanels = useMemo(() => {
    const out: ScreenPanel[] = [];
    for (const panel of p.panels) {
      const plan = geometry.get(panel.key);
      if (plan?.length) out.push({ key: panel.key, corners: plan.map((q) => enuToScreen(view, size.w, size.h, q)) });
    }
    return out;
  }, [p.panels, geometry, view, size]);
  const batches = useMemo(() => panelBatches(screenPanels, { selected: selectedKeys, conflicts: p.conflicts, heat: p.heat, fresh, snapped }), [screenPanels, selectedKeys, p.conflicts, p.heat, fresh, snapped]);
  const hoverPanelPath = hoverPanel ? ringPath(screenPanels.find((x) => x.key === hoverPanel)?.corners ?? []) : "";
  const panelCanvas = useRef<HTMLCanvasElement>(null);
  useLayoutEffect(() => {
    const c = panelCanvas.current;
    const g = c?.getContext("2d");
    if (!c || !g) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.round(size.w * dpr), h = Math.round(size.h * dpr);
    if (c.width !== w || c.height !== h) {
      c.width = w;
      c.height = h;
    }
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, size.w, size.h);
    drawPanels(g, batches, panelLook(c, g), (d) => new Path2D(d));
  }, [batches, size]);
  const hoverHandle = hovered?.kind === "handle" ? hovered.handle : null;
  const activeHandle = dragging?.kind === "gizmo" ? dragging.handle : null;
  const hoverShade = hovered?.kind === "shade" ? hovered.id : null;
  const hoverObstacle = hovered?.kind === "obstacle" ? hovered.id : null;
  const hoverKnob = hovered?.kind === "knob" ? hovered.knob : dragging?.kind === "knob" ? dragging.knob : null;
  const cursorClass =
    p.align ? (p.align.mode === "points" ? " is-aligning-points" : dragging?.kind === "align" ? " is-over-move" : " is-aligning") :
    hovered?.kind === "dormer" || dragging?.kind === "move-dormer" ? " is-over-move" :
    hoverKnob === "height" ? " is-over-height" : hoverKnob === "radius" ? " is-over-radius" : hoverHandle === "centre" || hoverPanel || hoverShade || hoverObstacle || dragging?.kind === "move-shade" || dragging?.kind === "move-obstacle" ? " is-over-move" : hoverHandle ? " is-over-handle" : dragging?.kind === "pan" ? " is-panning" : "";
  // shade tools: what a click (or the drag in progress) makes, from the same functions as the gesture
  const creating = dragging?.kind === "create" ? dragging : null;
  const shadeGhost = (() => {
    if (p.readOnly || !hover) return null;
    if (p.tool === "tree") {
      const c = creating ? creating.start : hover;
      const r = creating ? Math.hypot(creating.now[0] - c[0], creating.now[1] - c[1]) : 0;
      return { kind: "tree" as const, center: c, radius: r >= 0.4 ? r : nextTree(p.shadeOptions, c).radius ?? 3, sized: r >= 0.4 };
    }
    if (p.tool === "obstacle") {
      const kind = p.shadeOptions.obstacle;
      const size = creating ? Math.hypot(creating.now[0] - creating.start[0], creating.now[1] - creating.start[1]) : 0;
      const o = !creating || size < 0.15 ? nextObstacle(p.shadeOptions, creating ? creating.start : hover, houseDeg) : p.shadeOptions.shape === "cylinder" ? obstacleCylinder(kind, creating.start, size) : obstacleBox(kind, creating.start, creating.now, houseDeg);
      return { kind: "obstacle" as const, polygon: o.polygon, sized: !!creating && size >= 0.15 };
    }
    return null;
  })();
  const wallCursor = p.tool === "neighbour" && p.shadeOptions.neighbour === "wall" && hover && !p.readOnly ? (wallStart ? snapDirection(wallStart, hover, 45, alt ? 0 : 6, houseDeg) : hover) : null;
  const shadows = useMemo(() => (p.sun ? p.shade.map((x) => ({ key: x.key, ring: groundShadow(x, p.sun!) })).filter((x) => x.ring && x.ring.length >= 3) : []), [p.shade, p.sun]);
  const scale = scaleBar(tx, view.mpp);

  return (
    <div
      ref={host}
      className={`sk-canvas sk-tool-${p.tool}${cursorClass}`}
      onPointerDown={down}
      onPointerMove={move}
      onPointerUp={up}
      onPointerCancel={up}
      onPointerLeave={() => !drag.current && setHover(null)}
      onDoubleClick={() => setChain(null)}
      role="application"
      aria-label={tx.t("sketch.canvas.label")}
    >
      <svg width={size.w} height={size.h} className="sk-svg">
        <defs>
          <linearGradient id="sk-panel-fill" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="var(--sk-panel-a)" />
            <stop offset="1" stopColor="var(--sk-panel-b)" />
          </linearGradient>
          <linearGradient id="sk-panel-fill-selected" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#3a3a1a" />
            <stop offset="1" stopColor="#14122a" />
          </linearGradient>
          <pattern id="sk-conflict-hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width="6" height="6" fill="rgba(120, 10, 25, 0.85)" />
            <rect width="3" height="6" fill="rgba(255, 77, 94, 0.9)" />
          </pattern>
          <pattern id="sk-neighbour-hatch" width="7" height="7" patternUnits="userSpaceOnUse" patternTransform="rotate(-45)">
            <rect width="7" height="7" className="sk-neighbour-hatch-bg" />
            <rect width="1.6" height="7" className="sk-neighbour-hatch-line" />
          </pattern>
          <radialGradient id="sk-crown-deciduous" cx="0.38" cy="0.34" r="0.75">
            <stop offset="0" stopColor="var(--sk-tree-light)" />
            <stop offset="1" stopColor="var(--sk-tree)" />
          </radialGradient>
          <radialGradient id="sk-crown-evergreen" cx="0.38" cy="0.34" r="0.75">
            <stop offset="0" stopColor="var(--sk-pine-light)" />
            <stop offset="1" stopColor="var(--sk-pine)" />
          </radialGradient>
        </defs>
        {p.image?.url && imageMatrix && <image href={p.image.url} width={p.image.width} height={p.image.height} transform={imageMatrix} className="sk-basemap" preserveAspectRatio="none" />}
        {p.align?.url && (
          <image
            href={p.align.url}
            width={p.align.width}
            height={p.align.height}
            transform={matrixOf(p.align.m, view, size)}
            className="sk-align-picture"
            style={{ opacity: p.align.mode === "points" && p.align.points.length && !p.align.points[p.align.points.length - 1]!.enu ? Math.min(p.align.opacity, 0.25) : p.align.opacity }}
            preserveAspectRatio="none"
          />
        )}

        {shadows.length > 0 && (
          <g className="sk-shadows" aria-hidden>
            {shadows.map((x) => <path key={x.key} d={path(x.ring!)} />)}
          </g>
        )}

        <g className="sk-neighbours">
          {p.shade.filter((x) => x.kind !== "tree").map((x) => {
            const selected = p.selection?.kind === "shade" && p.selection.key === x.key;
            const c = S(ringCentre(x.polygon!));
            return (
              <g key={x.key} className={`sk-neighbour is-${x.kind}${selected ? " is-selected" : ""}${hoverShade === x.key ? " is-hover" : ""}`}>
                <path d={path(x.polygon!)} />
                {x.kind === "building" && <HeightTag at={c} height={x.height} />}
              </g>
            );
          })}
        </g>

        <g className="sk-faces">
          {p.sketch.faces.map((f, i) => {
            const pts = f.boundary.map((v) => p.sketch.vertices[v]!).filter(Boolean);
            if (pts.length < 3 || hidden.has(f.id)) return null;
            const selected = p.selection?.kind === "face" && p.selection.id === f.id;
            const other = !!p.activeStructure && (f.structureId || "main") !== p.activeStructure;
            return (
              <g key={f.id} className={`sk-face${selected ? " is-selected" : ""}${hoverFace === f.id ? " is-hover" : ""}${p.flashFaces?.has(f.id) ? " is-hinted" : ""}${other ? " is-other-structure" : ""}`} style={{ ["--sk-face-hue" as string]: FACE_HUE[i % FACE_HUE.length] }}>
                <path d={path(pts)} />
              </g>
            );
          })}
        </g>

        {p.hintBoxes && p.hintBoxes.length > 0 && (
          <g className="sk-hint-boxes" aria-hidden>
            {p.hintBoxes.map((b, i) => {
              const [lx, ly] = S(b.points[3] ?? b.points[0]!);
              return (
                <g key={i} className={`sk-hint-box${b.matched ? " is-matched" : ""}`}>
                  <path d={path(b.points)} />
                  <text x={lx + 4} y={ly + 12}>{b.label}</text>
                </g>
              );
            })}
          </g>
        )}

        <g className="sk-lines">
          {p.sketch.edges.map((e) => {
            const a = p.sketch.vertices[e.a];
            const b = p.sketch.vertices[e.b];
            if (!a || !b || !lineVisible(e.a, e.b)) return null;
            const selected = p.selection?.kind === "edge" && p.selection.id === e.id;
            const [x1, y1] = S(a);
            const [x2, y2] = S(b);
            return (
              <g key={e.id} className={`sk-edge${selected ? " is-selected" : ""}${hoverEdge === e.id ? " is-hover" : ""}`}>
                <line x1={x1} y1={y1} x2={x2} y2={y2} className="sk-line-halo" />
                <line x1={x1} y1={y1} x2={x2} y2={y2} className={`sk-line sk-line-${e.type}`} />
                {(selected || hoverEdge === e.id) && <Length a={a} b={b} S={S} />}
              </g>
            );
          })}
        </g>

        <g className="sk-dormers">
          {dormerShapes.map((d) => {
            const selected = p.selection?.kind === "dormer" && p.selection.id === d.id;
            const hot = hovered?.kind === "dormer" && hovered.id === d.id;
            return (
              <g key={d.id} className={`sk-dormer-shape${selected ? " is-selected" : ""}${hot ? " is-hover" : ""}${d.valid ? "" : " is-invalid"}`}>
                {d.box.length > 0 && <path d={path(d.box)} className="sk-dormer-box" />}
                {d.faces.map((f) => <path key={f.key} d={path(f.plan)} className={`sk-dormer-face is-${f.key}`} />)}
                {d.ridge && <line x1={S(d.ridge[0])[0]} y1={S(d.ridge[0])[1]} x2={S(d.ridge[1])[0]} y2={S(d.ridge[1])[1]} className="sk-line sk-line-ridge sk-dormer-ridge" />}
                {selected && d.box.length > 0 && <Length a={d.box[0]!} b={d.box[3]!} S={S} />}
              </g>
            );
          })}
        </g>

        <g className="sk-obstacles">
          {p.obstacles.map((o) => {
            const selected = p.selection?.kind === "obstacle" && p.selection.key === o.key;
            return (
              <g key={o.key} className={`sk-obstacle is-${o.kind} is-${shapeOf(o)}${selected ? " is-selected" : ""}${hoverObstacle === o.key ? " is-hover" : ""}`}>
                <path d={path(o.polygon)} />
              </g>
            );
          })}
        </g>

      </svg>
      {/* the modules: one canvas between the roof below and the tools above, so moving 500 of them repaints only it */}
      <canvas ref={panelCanvas} className={`sk-panels-canvas${p.heat ? " is-heat" : ""}`} style={{ width: size.w, height: size.h }} aria-hidden />
      <svg width={size.w} height={size.h} className="sk-svg sk-svg-over">
        <g className={`sk-panels-over${p.heat ? " is-heat" : ""}`}>
          {hoverPanelPath && <path d={hoverPanelPath} className="sk-panel is-hover" />}
          {batches.fresh && <path key={freshTick} d={batches.fresh} className="sk-panel is-fresh" />}
          {batches.snapped && <path d={batches.snapped} className="sk-panel is-snapped" />}
          {batches.labels.map((l) => <text key={l.key} className="sk-heat-label" x={l.at[0]} y={l.at[1]}>{l.text}</text>)}
        </g>

        <g className="sk-trees">
          {p.shade.filter((x) => x.kind === "tree").map((x) => {
            const selected = p.selection?.kind === "shade" && p.selection.key === x.key;
            const c = S(x.center!);
            const r = (x.radius ?? 3) / view.mpp;
            return (
              <g key={x.key} className={`sk-tree is-${x.leaf} is-${x.crown}${selected ? " is-selected" : ""}${hoverShade === x.key ? " is-hover" : ""}`}>
                <circle cx={c[0]} cy={c[1]} r={r} className="sk-crown" fill={`url(#sk-crown-${x.leaf})`} />
                {x.crown === "conical" ? (
                  [0.66, 0.33].map((k) => <circle key={k} cx={c[0]} cy={c[1]} r={r * k} className="sk-crown-ring" />)
                ) : (
                  <circle cx={c[0] - r * 0.18} cy={c[1] - r * 0.2} r={r * 0.45} className="sk-crown-light" />
                )}
                <circle cx={c[0]} cy={c[1]} r={3} className="sk-trunk" />
                {!selected && r > 14 && <HeightTag at={[c[0], c[1] + Math.min(r * 0.55, r - 10)]} height={x.height} />}
              </g>
            );
          })}
        </g>

        {ghost && <path d={path(ghost)} className={`sk-ghost${p.placeMode === "free" ? " is-free" : ""}`} />}

        <g className="sk-tags">
          {p.sketch.faces.map((f) => {
            const pts = f.boundary.map((v) => p.sketch.vertices[v]!).filter(Boolean);
            const slope = p.slopes[f.id];
            if (pts.length < 3 || !slope || hidden.has(f.id) || (showGizmo && gizmo?.face.id === f.id)) return null;
            const selected = p.selection?.kind === "face" && p.selection.id === f.id;
            return <FaceTag key={f.id} at={S(centroid(pts))} slope={slope} selected={selected} />;
          })}
        </g>

        <g className="sk-vertices">
          {Object.entries(p.sketch.vertices).map(([id, v]) => {
            const selected = p.selection?.kind === "vertex" && p.selection.id === id;
            const hot = hoverVertex === id;
            return <circle key={id} cx={S(v)[0]} cy={S(v)[1]} r={selected || hot ? 6.5 : 4.5} className={`sk-vertex${selected ? " is-selected" : ""}${hot ? " is-hover" : ""}`} />;
          })}
        </g>

        {/* shade tools: the tree or obstacle a click (or this drag) makes, with its size */}
        {shadeGhost?.kind === "tree" && (
          <g className={`sk-shade-ghost is-tree${shadeGhost.sized ? " is-sized" : ""}`}>
            <circle cx={S(shadeGhost.center)[0]} cy={S(shadeGhost.center)[1]} r={shadeGhost.radius / view.mpp} />
            <circle cx={S(shadeGhost.center)[0]} cy={S(shadeGhost.center)[1]} r={3} className="sk-trunk" />
            {shadeGhost.sized && (
              <>
                <line x1={S(shadeGhost.center)[0]} y1={S(shadeGhost.center)[1]} x2={S(shadeGhost.center)[0] + shadeGhost.radius / view.mpp} y2={S(shadeGhost.center)[1]} className="sk-guide" />
                <Cue at={[S(shadeGhost.center)[0] + shadeGhost.radius / view.mpp, S(shadeGhost.center)[1]]} label={tx.t("sketch.canvas.crown", { size: tx.num(shadeGhost.radius * 2, 1) })} kind="vertex" />
              </>
            )}
          </g>
        )}
        {shadeGhost?.kind === "obstacle" && (
          <g className={`sk-shade-ghost is-obstacle${shadeGhost.sized ? " is-sized" : ""}`}>
            <path d={path(shadeGhost.polygon)} />
            {hover && <Cue at={S(hover)} label={shadeGhost.sized ? tx.t("sketch.readout.obstacleSize", { label: tx.t(OBSTACLE_PRESETS[p.shadeOptions.obstacle].label), size: obstacleSize(tx, shadeGhost.polygon, p.shadeOptions.shape) }) : tx.t(OBSTACLE_PRESETS[p.shadeOptions.obstacle].label)} kind="edge" />}
          </g>
        )}
        {wallCursor && (
          <g className="sk-drawing">
            {wallStart && (
              <>
                <line x1={S(wallStart)[0]} y1={S(wallStart)[1]} x2={S(wallCursor)[0]} y2={S(wallCursor)[1]} className="sk-line-halo" />
                <line x1={S(wallStart)[0]} y1={S(wallStart)[1]} x2={S(wallCursor)[0]} y2={S(wallCursor)[1]} className="sk-wall-preview" />
                <Length a={wallStart} b={wallCursor} S={S} />
                <circle cx={S(wallStart)[0]} cy={S(wallStart)[1]} r={5} className="sk-corner" />
              </>
            )}
            <circle cx={S(wallCursor)[0]} cy={S(wallCursor)[1]} r={5} className="sk-corner" />
            {!wallStart && <Cue at={S(wallCursor)} label={tx.t("sketch.canvas.wallStart")} kind="square" />}
          </g>
        )}

        {showGizmo && gizmo && (
          <Gizmo2D gizmo={gizmo} S={S} mpp={view.mpp} hover={hoverHandle} active={activeHandle} turn={turn} mode={p.placeMode} />
        )}

        {/* line tool: the line a click would draw, its length and what it snaps to */}
        {drawSnap && (
          <g className="sk-guides" aria-hidden>
            {drawSnap.guides.map((g, i) => <GuideLine key={i} g={g} S={S} />)}
          </g>
        )}
        {lineCursor && (
          <g className="sk-drawing">
            {chain && (
              <>
                <line x1={S(chain.point)[0]} y1={S(chain.point)[1]} x2={S(lineCursor.snap.point)[0]} y2={S(lineCursor.snap.point)[1]} className="sk-line-halo" />
                <line x1={S(chain.point)[0]} y1={S(chain.point)[1]} x2={S(lineCursor.snap.point)[0]} y2={S(lineCursor.snap.point)[1]} className={`sk-line sk-line-${p.lineType} is-preview`} />
                <Length a={chain.point} b={lineCursor.snap.point} S={S} />
                <circle cx={S(chain.point)[0]} cy={S(chain.point)[1]} r={5} className="sk-snap-dot" />
              </>
            )}
            <SmartCue at={S(lineCursor.snap.point)} snap={drawSnap!} />
          </g>
        )}

        {/* footprint (or a neighbouring building): the outline so far, the next corner and the closing cue */}
        {(p.tool === "footprint" || p.tool === "neighbour") && corners.length > 0 && (
          <g className={`sk-drawing${p.tool === "neighbour" ? " is-neighbour" : ""}`}>
            {(() => {
              const pts = [...corners, ...(footCursor && !closing ? [footCursor] : [])];
              return (
                <>
                  {pts.length >= 3 && <path d={path(pts)} className="sk-footprint-fill" />}
                  {footSquared && footCursor && !closing && <Guide a={corners[corners.length - 1]!} b={footCursor} S={S} />}
                  <path d={path(closing ? [...corners, corners[0]!] : pts, false)} className="sk-footprint-halo" />
                  <path d={path(closing ? [...corners, corners[0]!] : pts, false)} className="sk-footprint-preview" />
                  {pts.slice(1).map((q, i) => <Length key={i} a={pts[i]!} b={q} S={S} />)}
                  {closing && <Length a={corners[corners.length - 1]!} b={corners[0]!} S={S} />}
                  {corners.map((q, i) => (
                    <circle key={i} cx={S(q)[0]} cy={S(q)[1]} r={i === 0 ? 7 : 5} className={`sk-corner${i === 0 ? " is-first" : ""}${i === 0 && closing ? " is-closing" : ""}`} />
                  ))}
                  {footCursor && !closing && <circle cx={S(footCursor)[0]} cy={S(footCursor)[1]} r={5} className="sk-corner" />}
                  {closing && <Cue at={S(corners[0]!)} label={tx.t("sketch.canvas.close")} kind="close" />}
                  {footSquared && !closing && footCursor && <Cue at={S(footCursor)} label="90°" kind="square" />}
                  {p.tool === "footprint" && drawSnap && !closing && footCursor && <SmartCue at={S(footCursor)} snap={drawSnap} />}
                </>
              );
            })()}
          </g>
        )}
        {p.tool === "footprint" && corners.length === 0 && footCursor && (
          <>
            <circle cx={S(footCursor)[0]} cy={S(footCursor)[1]} r={5} className="sk-corner" />
            {drawSnap && drawSnap.kind !== "free" && <SmartCue at={S(footCursor)} snap={drawSnap} />}
          </>
        )}
        {p.tool === "neighbour" && p.shadeOptions.neighbour === "building" && corners.length === 0 && footCursor && <circle cx={S(footCursor)[0]} cy={S(footCursor)[1]} r={5} className="sk-corner" />}

        {p.align && <AlignHandles a={p.align} S={S} />}
        {p.footprintSide && p.selection?.kind === "edge" && p.onToggleSide && (() => {
          const e = p.sketch.edges.find((x) => x.id === (p.selection as { id: string }).id);
          const a = e && p.sketch.vertices[e.a];
          const b = e && p.sketch.vertices[e.b];
          if (!a || !b) return null;
          // beside the side, away from the roof (so it never covers the faces' tags)
          const pts = Object.values(p.sketch.vertices);
          const c = pts.reduce((s, q) => [s[0] + q[0] / pts.length, s[1] + q[1] / pts.length], [0, 0]);
          const mid: Vec2 = [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
          let nx = -(b[1] - a[1]), ny = b[0] - a[0];
          if (nx * (mid[0] - c[0]) + ny * (mid[1] - c[1]) < 0) [nx, ny] = [-nx, -ny];
          const l = Math.hypot(nx, ny) || 1;
          const m = S(mid);
          return <SideChip at={[m[0] + (nx / l) * 92, m[1] - (ny / l) * 40 + 30]} side={p.footprintSide} onToggle={p.onToggleSide} />;
        })()}

        {p.gaps && p.gaps.list.length > 0 && <GapMarks gaps={p.gaps} sketch={p.sketch} S={S} onSelect={p.gaps.onSelect} />}

        {/* the selected shade source's gizmo: radius knob on its rim, height knob on a stem */}
        {knobs && (
          <ShadeKnobs
            anchor={S(knobs.anchor)}
            radius={knobs.radius != null ? S([knobs.anchor[0] + knobs.radius, knobs.anchor[1]]) : null}
            radiusM={knobs.radius}
            height={knobs.height}
            hover={hoverKnob}
            ring={selShade?.kind === "tree" ? { r: (selShade.radius ?? 0) / view.mpp, base: crownBase(selShade) } : null}
          />
        )}
      </svg>
      <div className="sk-vignette" aria-hidden />
      {readoutAt && (
        <div key={readoutAt.n} className="sk-wheel-readout" style={{ left: readoutAt.x + 18, top: readoutAt.y - 38 }} role="status" aria-live="polite">
          {readoutAt.text}
        </div>
      )}
      {drawSnap && hover && anchor && (
        <div className="sk-measure" style={{ left: S(drawSnap.point)[0] + 16, top: S(drawSnap.point)[1] + 14 }} aria-live="polite">
          {typed ? (
            <>
              <span className="sk-measure-typed">{typed}<i className="sk-caret" /></span>
              <span className="sk-measure-unit">{typed.includes("<") || typed.includes(" ") ? "m · °" : "m"}</span>
              {typedTooLong(typed) ? (
                <span className="sk-measure-error" role="alert">{tx.t("sketch.canvas.typedTooLong", { max: tx.num(MAX_TYPED_M, 0) })}</span>
              ) : (
                <kbd className="sk-kbd">{KEY_ENTER}</kbd>
              )}
            </>
          ) : (
            <>
              <span>{measureLabel(tx, drawSnap)}</span>
              <span className="sk-measure-hint">{tx.t("sketch.canvas.typeLength")}</span>
            </>
          )}
        </div>
      )}
      {(p.notes ?? []).map((n) => {
        const [x, y] = S(n.at);
        return (
          <button
            key={n.id}
            type="button"
            className={`sk-note-pin${n.selected ? " is-selected" : ""}`}
            style={{ left: x, top: y }}
            aria-label={tx.t("sketch.canvas.note", { n: n.n })}
            title={tx.t("sketch.canvas.note", { n: n.n })}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={() => p.onNote?.(n.id)}
          >
            <span>{n.n}</span>
          </button>
        );
      })}
      <div className="sk-scale" aria-hidden>
        <span>{scale.label}</span>
        <i style={{ width: scale.px }} />
      </div>
      <div className="sk-attribution">{p.attribution}</div>
      <div className="sk-map-controls">
        <div className="sk-north sk-glass" title={tx.t("sketch.canvas.northUp")} aria-hidden>
          <svg width="22" height="22" viewBox="0 0 22 22">
            <path d="M11 2 L15 12 L11 10 L7 12 Z" fill="#ff4d5e" />
            <path d="M11 20 L15 12 L11 13.5 L7 12 Z" fill="currentColor" opacity="0.35" />
          </svg>
        </div>
        <div className="sk-zoom sk-glass">
          <button type="button" onClick={() => setView((v) => ({ ...v, mpp: clampMpp(v.mpp / 1.3) }))} aria-label={tx.t("sketch.canvas.zoomIn")} title={tx.t("sketch.canvas.zoomIn")}><Plus size={17} /></button>
          <button type="button" onClick={() => setView((v) => ({ ...v, mpp: clampMpp(v.mpp * 1.3) }))} aria-label={tx.t("sketch.canvas.zoomOut")} title={tx.t("sketch.canvas.zoomOut")}><Minus size={17} /></button>
          <button type="button" onClick={frame} aria-label={tx.t("sketch.canvas.fit")} title={tx.t("sketch.canvas.fit")}><Maximize2 size={15} /></button>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the roof check on the map

/** The gaps, numbered as in the side panel: a pulsing ring where each is, the lines involved in red, and for the
 * selected one where its fix puts the corner (dashed). */
function GapMarks({ gaps, sketch, S, onSelect }: { gaps: NonNullable<Props["gaps"]>; sketch: Sketch; S: (q: Vec2) => Vec2; onSelect: (id: string | null) => void }) {
  const edgeLine = (id: string) => {
    const e = sketch.edges.find((x) => x.id === id);
    const a = e && sketch.vertices[e.a];
    const b = e && sketch.vertices[e.b];
    if (!a || !b) return null;
    return <line key={id} x1={S(a)[0]} y1={S(a)[1]} x2={S(b)[0]} y2={S(b)[1]} className="sk-gap-line" />;
  };
  const select = (id: string, sel: boolean) => (e: ReactPointerEvent) => {
    e.stopPropagation();
    onSelect(sel ? null : id);
  };
  // two passes: every gap's lines first, then the numbered rings on top (a line of one gap never covers another's ring)
  return (
    <g className="sk-gap-marks">
      {gaps.list.map((g) => {
        const sel = gaps.selected === g.id;
        const c = S(g.at);
        const lines = g.kind === "height" ? sketch.edges.filter((e) => e.a === g.vertices[0] || e.b === g.vertices[0]).map((e) => e.id) : g.kind === "sliver" ? [] : g.edges;
        const sliver = g.kind === "sliver" ? sketch.faces.find((f) => f.id === g.faces[0]) : null;
        return (
          <g key={g.id} className={`sk-gap-lines is-${g.kind}${sel ? " is-selected" : ""}`} onPointerDown={select(g.id, sel)}>
            {sliver && <path d={sliver.boundary.map((v, k) => `${k ? "L" : "M"}${S(sketch.vertices[v]!)[0]},${S(sketch.vertices[v]!)[1]}`).join("") + "Z"} className="sk-gap-sliver" />}
            {lines.map(edgeLine)}
            {g.kind === "weld" && g.vertices.slice(1).map((v) => sketch.vertices[v] && <line key={v} x1={c[0]} y1={c[1]} x2={S(sketch.vertices[v]!)[0]} y2={S(sketch.vertices[v]!)[1]} className="sk-gap-join" />)}
          </g>
        );
      })}
      {gaps.list.map((g, i) => {
        const sel = gaps.selected === g.id;
        const c = S(g.at);
        const t = g.target ? S(g.target) : null;
        return (
          <g key={g.id} className={`sk-gap-mark is-${g.kind}${sel ? " is-selected" : ""}${g.fixable ? "" : " is-stuck"}`} onPointerDown={select(g.id, sel)}>
            {t && sel && Math.hypot(t[0] - c[0], t[1] - c[1]) > 1 && (
              <>
                <line x1={c[0]} y1={c[1]} x2={t[0]} y2={t[1]} className="sk-gap-join" />
                <circle cx={t[0]} cy={t[1]} r={4.5} className="sk-gap-target" />
              </>
            )}
            <circle cx={c[0]} cy={c[1]} r={15} className="sk-gap-pulse" />
            <circle cx={c[0]} cy={c[1]} r={10.5} className="sk-gap-ring" />
            <text x={c[0]} y={c[1]} className="sk-gap-num">{i + 1}</text>
          </g>
        );
      })}
    </g>
  );
}

// ------------------------------------------------------------------ pieces of the drawing

const stemPx = (height: number) => Math.max(STEM_MIN_PX, Math.min(150, height * STEM_PX_PER_M));

function obstacleSize(tx: Text, ring: Vec2[], shape: string): string {
  const c = ringCentre(ring);
  if (shape === "cylinder") return tx.t("sketch.readout.diameter", { size: tx.t("sketch.unit.m", { value: tx.num(2 * Math.max(...ring.map((q) => Math.hypot(q[0] - c[0], q[1] - c[1]))), 2) }) });
  const a = ring[0]!, b = ring[1]!, d = ring[3]!;
  return tx.t("sketch.readout.box", { width: tx.num(Math.hypot(b[0] - a[0], b[1] - a[1]), 2), depth: tx.num(Math.hypot(d[0] - a[0], d[1] - a[1]), 2) });
}

/** A small height tag ("10 m") on a tree or a neighbouring building. */
function HeightTag({ at, height }: { at: Vec2; height: number }) {
  const tx = useSketchText();
  const label = tx.t("sketch.unit.m", { value: tx.num(height < 10 ? height : Math.round(height), height < 10 ? 1 : 0) });
  const w = labelWidth(label, 6) + 12;
  return (
    <g className="sk-height-tag" transform={`translate(${at[0]},${at[1]})`}>
      <rect x={-w / 2} y={-8} width={w} height={16} rx={8} />
      <text>{label}</text>
    </g>
  );
}

/** The shade gizmo: a dashed rim with the radius knob (trees, round obstacles) and a vertical stem whose knob sets
 * the height (drag up or down), both labelled. */
function ShadeKnobs({ anchor, radius, radiusM, height, hover, ring }: { anchor: Vec2; radius: Vec2 | null; radiusM: number | null; height: number; hover: "radius" | "height" | null; ring: { r: number; base: number } | null }) {
  const tx = useSketchText();
  const top = anchor[1] - stemPx(height);
  const h = tx.num(height, height < 10 ? 1 : 0);
  const hLabel = ring ? tx.t("sketch.canvas.heightCrownFrom", { height: h, base: tx.num(ring.base, 1) }) : tx.t("sketch.unit.m", { value: h });
  const hw = labelWidth(hLabel, 6.1) + 16;
  return (
    <g className="sk-shade-gizmo">
      {ring && <circle cx={anchor[0]} cy={anchor[1]} r={ring.r} className="sk-shade-rim" />}
      <line x1={anchor[0]} y1={anchor[1]} x2={anchor[0]} y2={top} className="sk-shade-stem-halo" />
      <line x1={anchor[0]} y1={anchor[1]} x2={anchor[0]} y2={top} className="sk-shade-stem" />
      <circle cx={anchor[0]} cy={anchor[1]} r={4} className="sk-shade-foot" />
      <g className={`sk-shade-knob is-height${hover === "height" ? " is-active" : ""}`} transform={`translate(${anchor[0]},${top})`}>
        <circle r={10} />
        <path d="M0,-5 L3.5,-1.5 L-3.5,-1.5 Z M0,5 L3.5,1.5 L-3.5,1.5 Z" />
      </g>
      <g className="sk-gizmo-label" transform={`translate(${anchor[0] + 18 + hw / 2},${top})`}>
        <rect x={-hw / 2} y={-10} width={hw} height={20} rx={10} />
        <text>{hLabel}</text>
      </g>
      {radius && radiusM != null && (
        <>
          <line x1={anchor[0]} y1={anchor[1]} x2={radius[0]} y2={radius[1]} className="sk-guide" />
          <g className={`sk-shade-knob is-radius${hover === "radius" ? " is-active" : ""}`} transform={`translate(${radius[0]},${radius[1]})`}>
            <circle r={9} />
            <path d="M-5,0 L-1.5,-3.5 L-1.5,3.5 Z M5,0 L1.5,-3.5 L1.5,3.5 Z" />
          </g>
          <g className="sk-gizmo-label" transform={`translate(${radius[0]},${radius[1] + 22})`}>
            <rect x={-34} y={-10} width={68} height={20} rx={10} />
            <text>{tx.t("sketch.readout.diameter", { size: tx.t("sketch.unit.m", { value: tx.num(radiusM * 2, 1) }) })}</text>
          </g>
        </>
      )}
    </g>
  );
}

/** The corner a footprint click adds: square to the last side (or to the house), Alt free. */
function footprintCorner(corners: Vec2[], q: Vec2, free: boolean, houseDeg: number): Vec2 {
  const n = corners.length;
  if (!n) return q;
  const ref = n >= 2 ? Math.atan2(corners[n - 1]![1] - corners[n - 2]![1], corners[n - 1]![0] - corners[n - 2]![0]) / DEG : houseDeg;
  return snapDirection(corners[n - 1]!, q, 90, free ? 0 : 6, ref);
}

/** A length label at the middle of a segment, kept upright. */
function Length({ a, b, S }: { a: Vec2; b: Vec2; S: (q: Vec2) => Vec2 }) {
  const tx = useSketchText();
  const m = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const [x1, y1] = S(a);
  const [x2, y2] = S(b);
  if (Math.hypot(x2 - x1, y2 - y1) < 46 || m < 0.05) return null;
  const label = tx.t("sketch.unit.m", { value: tx.num(m, m < 10 ? 2 : 1) });
  const w = labelWidth(label, 6.2) + 12;
  // offset to the side of the line so it never hides it
  const nx = -(y2 - y1), ny = x2 - x1;
  const l = Math.hypot(nx, ny) || 1;
  const cx = (x1 + x2) / 2 + (nx / l) * 14;
  const cy = (y1 + y2) / 2 + (ny / l) * 14;
  return (
    <g className="sk-length" transform={`translate(${cx},${cy})`}>
      <rect x={-w / 2} y={-9} width={w} height={18} rx={9} />
      <text>{label}</text>
    </g>
  );
}

/** A dashed guide through a segment, both ways: the direction snapped square. */
function Guide({ a, b, S }: { a: Vec2; b: Vec2; S: (q: Vec2) => Vec2 }) {
  const [x1, y1] = S(a);
  const [x2, y2] = S(b);
  const l = Math.hypot(x2 - x1, y2 - y1) || 1;
  const k = 4000 / l;
  return <line x1={x1 - (x2 - x1) * k} y1={y1 - (y2 - y1) * k} x2={x2 + (x2 - x1) * k} y2={y2 + (y2 - y1) * k} className="sk-guide" />;
}

function Cue({ at, label, kind }: { at: Vec2; label: string; kind: "vertex" | "edge" | "square" | "close" }) {
  const w = labelWidth(label, 6.4) + 14;
  return (
    <g className={`sk-cue is-${kind}`} transform={`translate(${at[0] + 14 + w / 2},${at[1] - 16})`}>
      <rect x={-w / 2} y={-9} width={w} height={18} rx={9} />
      <text>{label}</text>
    </g>
  );
}

export const pitchRise = (deg: number) => Math.round(12 * Math.tan(deg * DEG) * 2) / 2;

/** A face's tag: pitch as rise/12 and degrees, facing, with a down-slope arrow. */
function FaceTag({ at, slope, selected }: { at: Vec2; slope: Slope; selected: boolean }) {
  const tx = useSketchText();
  const flat = slope.pitch <= 0.5;
  const main = flat ? tx.t("sketch.pitch.flat") : tx.t("sketch.pitch.rise", { rise: tx.upTo(pitchRise(slope.pitch), 1) });
  const dim = flat ? "" : ` ${tx.t("sketch.canvas.faceTagDim", { deg: tx.num(Math.round(slope.pitch), 0), dir: compassName(tx, slope.azimuth) })}`;
  const textW = labelWidth(main + dim, 6.3);
  const w = textW + (flat ? 18 : 34);
  return (
    <g className="sk-tag" transform={`translate(${at[0]},${at[1]})`}>
      <rect x={-w / 2} y={-12} width={w} height={24} rx={12} className={selected ? "is-selected" : undefined} />
      {!flat && (
        <g transform={`translate(${-w / 2 + 14},0) rotate(${slope.azimuth})`}>
          <circle r={8} className="sk-tag-dial" />
          <path d="M0,6 L4,0.5 L1.5,0.5 L1.5,-5 L-1.5,-5 L-1.5,0.5 L-4,0.5 Z" transform="rotate(180)" className="sk-face-arrow" />
        </g>
      )}
      <text x={flat ? 0 : 8}>
        {main}
        {dim && <tspan className="sk-tag-dim">{dim}</tspan>}
      </text>
    </g>
  );
}

/** Half-cut module look: two cell columns and the split across the middle, in screen space. */

/** The ring gizmo: ring with 15° ticks, the move disc, ±u/±v arrows (axis colours) and the turn arc. */
function Gizmo2D({ gizmo, S, mpp, hover, active, turn, mode }: {
  gizmo: NonNullable<ReturnType<typeof gizmoFrame>>;
  S: (q: Vec2) => Vec2;
  mpp: number;
  hover: Handle | null;
  active: Handle | null;
  turn: { from: number; screenDelta: number; deg: number } | null;
  mode: PlaceMode;
}) {
  const tx = useSketchText();
  const c = S(gizmo.centrePlan);
  const r = gizmo.radius / mpp;
  const hs = handles(gizmo);
  const rot = hs.find((h) => h.handle === "rotate")!;
  const ra = Math.atan2(S(rot.at)[1] - c[1], S(rot.at)[0] - c[0]);
  const arc = (a0: number, a1: number, rr: number) => {
    const p0 = [c[0] + rr * Math.cos(a0), c[1] + rr * Math.sin(a0)];
    const p1 = [c[0] + rr * Math.cos(a1), c[1] + rr * Math.sin(a1)];
    const large = Math.abs(a1 - a0) > Math.PI ? 1 : 0;
    return `M${p0[0]},${p0[1]} A${rr},${rr} 0 ${large} ${a1 > a0 ? 1 : 0} ${p1[0]},${p1[1]}`;
  };
  const head = (a: number, dir: 1 | -1) => {
    // an arrowhead on the ring at angle a, pointing along the ring
    const x = c[0] + r * Math.cos(a), y = c[1] + r * Math.sin(a);
    const t = a + (dir * Math.PI) / 2;
    return `translate(${x},${y}) rotate(${(t * 180) / Math.PI})`;
  };
  const ticks = Array.from({ length: 24 }, (_, i) => {
    const a = (i * 15 * Math.PI) / 180;
    const k = i % 6 === 0 ? 7 : 4;
    return `M${c[0] + (r - k) * Math.cos(a)},${c[1] + (r - k) * Math.sin(a)}L${c[0] + r * Math.cos(a)},${c[1] + r * Math.sin(a)}`;
  }).join("");
  const label = turn ? `${turn.deg > 0 ? "+" : ""}${tx.num(Math.round(turn.deg), 0)}°` : tx.t(mode === "magnetic" ? "sketch.canvas.gizmoMagnetic" : "sketch.canvas.gizmoFree");
  const lw = labelWidth(label, 6.1) + 16;
  return (
    <g className="sk-gizmo">
      <circle cx={c[0]} cy={c[1]} r={r} className="sk-gizmo-halo" />
      {turn && Math.abs(turn.screenDelta) > 0.001 && (
        <path d={`M${c[0]},${c[1]} L${c[0] + r * Math.cos(turn.from)},${c[1] + r * Math.sin(turn.from)} ${arc(turn.from, turn.from + turn.screenDelta, r).replace(/^M[^A]+/, "")} Z`} className="sk-gizmo-wedge" />
      )}
      <circle cx={c[0]} cy={c[1]} r={r} className="sk-gizmo-ring" />
      <path d={ticks} className="sk-gizmo-ticks" />
      {hs.filter((h) => h.handle !== "rotate").map((h) => {
        const [x, y] = S(h.at);
        const ang = (Math.atan2(y - c[1], x - c[0]) * 180) / Math.PI;
        const axis = h.handle.endsWith("u") ? "u" : "v";
        return (
          <g key={h.handle} transform={`translate(${x},${y})`} className={`sk-gizmo-handle sk-gizmo-arrow sk-gizmo-arrow-${axis}${hover === h.handle || active === h.handle ? " is-active" : ""}`}>
            <g className="sk-gizmo-knob">
              <circle r={12} />
              <path transform={`rotate(${ang})`} d="M5.5,0 L-3,-5.5 L-1,0 L-3,5.5 Z" />
            </g>
          </g>
        );
      })}
      <g className={`sk-gizmo-handle sk-gizmo-rotate${hover === "rotate" || active === "rotate" ? " is-active" : ""}`}>
        <path d={arc(ra - ARC_DEG * DEG, ra + ARC_DEG * DEG, r)} className="sk-gizmo-arc-hit" />
        <path d={arc(ra - ARC_DEG * DEG, ra + ARC_DEG * DEG, r)} className="sk-gizmo-arc" />
        <path transform={head(ra + ARC_DEG * DEG, 1)} d="M0,-7 L8,0 L0,7 Z" className="sk-gizmo-arrowhead" />
        <path transform={head(ra - ARC_DEG * DEG, -1)} d="M0,-7 L8,0 L0,7 Z" className="sk-gizmo-arrowhead" />
        {/* the knob the review scripts and keyboard users find */}
        <circle cx={c[0] + r * Math.cos(ra)} cy={c[1] + r * Math.sin(ra)} r={6} fill="var(--sk-turn)" stroke="#fff" strokeWidth={2} />
      </g>
      <circle cx={c[0]} cy={c[1]} r={hover === "centre" || active === "centre" ? 15 : 13} className="sk-gizmo-centre" />
      <path
        className="sk-gizmo-centre-icon"
        transform={`translate(${c[0]},${c[1]})`}
        d="M0,-7 L0,7 M-7,0 L7,0 M-2.5,-4.5 L0,-7 L2.5,-4.5 M-2.5,4.5 L0,7 L2.5,4.5 M-4.5,-2.5 L-7,0 L-4.5,2.5 M4.5,-2.5 L7,0 L4.5,2.5"
      />
      <g className={`sk-gizmo-label${turn ? " is-turn" : ""}`} transform={`translate(${c[0]},${c[1] + r + 26})`}>
        <rect x={-lw / 2} y={-10} width={lw} height={20} rx={10} />
        <text>{label}</text>
      </g>
    </g>
  );
}

function scaleBar(tx: Text, mpp: number): { label: string; px: number } {
  const target = 110 * mpp;
  const steps = [0.5, 1, 2, 5, 10, 20, 50, 100, 200];
  const m = steps.reduce((best, s) => (Math.abs(s - target) < Math.abs(best - target) ? s : best), steps[0]!);
  return { label: tx.t("sketch.unit.m", { value: tx.upTo(m, 1) }), px: Math.round(m / mpp) };
}

function angleDiff(a: number, b: number): number {
  let d = a - b;
  while (d > Math.PI) d -= 2 * Math.PI;
  while (d < -Math.PI) d += 2 * Math.PI;
  return d;
}

function inside([x, y]: Vec2, ring: Vec2[]): boolean {
  let hit = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) hit = !hit;
  }
  return hit;
}

/** The ring's handles in plan: arrows along ±u and ±v (the face's eave and slope), and a turn knob. */
function handles(g: NonNullable<ReturnType<typeof gizmoFrame>>): { handle: Handle; at: Vec2 }[] {
  const u: Vec2 = norm([g.face.frame.u[0], g.face.frame.u[1]]);
  const v: Vec2 = norm([g.face.frame.v[0], g.face.frame.v[1]]);
  const r = g.radius;
  const c = g.centrePlan;
  const at = (d: Vec2, k: number): Vec2 => [c[0] + d[0] * r * k, c[1] + d[1] * r * k];
  const diag = norm([u[0] + v[0], u[1] + v[1]]);
  return [
    { handle: "+u", at: at(u, 1) },
    { handle: "-u", at: at(u, -1) },
    { handle: "+v", at: at(v, 1) },
    { handle: "-v", at: at(v, -1) },
    { handle: "rotate", at: at(diag, 1) },
  ];
}

function norm(v: Vec2): Vec2 {
  const l = Math.hypot(v[0], v[1]) || 1;
  return [v[0] / l, v[1] / l];
}

// ------------------------------------------------------------------ phase 4: dormers, snaps, the picture placement
const EMPTY_SET: ReadonlySet<string> = new Set();


/** The SVG matrix that draws a picture placed by `m` (pixels → metres) in the view. */
function matrixOf(m: Affine, view: View, size: { w: number; h: number }): string {
  const [A, B, C, D, E, F] = m;
  const k = 1 / view.mpp;
  return `matrix(${A * k} ${-D * k} ${B * k} ${-E * k} ${(C - view.cx) * k + size.w / 2} ${-(F - view.cy) * k + size.h / 2})`;
}

/** The picture's turn knob: beyond the middle of its top side. */
function turnKnob(box: Vec2[]): Vec2 {
  const [tl, tr, , bl] = box as [Vec2, Vec2, Vec2, Vec2];
  const top: Vec2 = [(tl[0] + tr[0]) / 2, (tl[1] + tr[1]) / 2];
  const up: Vec2 = [tl[0] - bl[0], tl[1] - bl[1]];
  const l = Math.hypot(up[0], up[1]) || 1;
  const k = Math.min(3, l * 0.12);
  return [top[0] + (up[0] / l) * k, top[1] + (up[1] / l) * k];
}

function AlignHandles({ a, S }: { a: AlignState; S: (q: Vec2) => Vec2 }) {
  const box = pictureCorners(a.m, a.width, a.height);
  const scr = box.map(S);
  const knob = S(turnKnob(box));
  const topMid: Vec2 = [(scr[0]![0] + scr[1]![0]) / 2, (scr[0]![1] + scr[1]![1]) / 2];
  return (
    <g className={`sk-align-handles is-${a.mode}`}>
      <path d={scr.map((q, i) => `${i ? "L" : "M"}${q[0].toFixed(1)},${q[1].toFixed(1)}`).join("") + "Z"} className="sk-align-frame" />
      {a.mode === "drag" && (
        <>
          <line x1={topMid[0]} y1={topMid[1]} x2={knob[0]} y2={knob[1]} className="sk-align-stem" />
          <circle cx={knob[0]} cy={knob[1]} r={9} className="sk-align-turn" />
          {scr.map((q, i) => <rect key={i} x={q[0] - 6} y={q[1] - 6} width={12} height={12} rx={2} className="sk-align-scale" />)}
        </>
      )}
      {a.points.map((pt, i) => {
        const onPicture = S(applyAffine(a.m, pt.image));
        const onMap = pt.enu ? S(pt.enu) : null;
        return (
          <g key={i} className="sk-align-point">
            {onMap && <line x1={onPicture[0]} y1={onPicture[1]} x2={onMap[0]} y2={onMap[1]} className="sk-align-link" />}
            <circle cx={onPicture[0]} cy={onPicture[1]} r={7} className="sk-align-dot is-picture" />
            {onMap && <circle cx={onMap[0]} cy={onMap[1]} r={7} className="sk-align-dot is-map" />}
            <text x={(onMap ?? onPicture)[0] + 11} y={(onMap ?? onPicture)[1] - 9} className="sk-align-num">{i + 1}</text>
          </g>
        );
      })}
    </g>
  );
}

/** A snapping guide (a line through the view). */
function GuideLine({ g, S }: { g: { from: Vec2; dir: Vec2; kind: string }; S: (q: Vec2) => Vec2 }) {
  const a = S([g.from[0] - g.dir[0] * 400, g.from[1] - g.dir[1] * 400]);
  const b = S([g.from[0] + g.dir[0] * 400, g.from[1] + g.dir[1] * 400]);
  return <line x1={a[0]} y1={a[1]} x2={b[0]} y2={b[1]} className={`sk-guide sk-guide-${g.kind}`} />;
}

const SNAP_CUE: Record<string, "vertex" | "edge" | "square" | "close"> = { corner: "vertex", midpoint: "edge", intersection: "square", "on-line": "edge", extension: "square", perpendicular: "square", parallel: "square", angle: "square", typed: "close" };

/** What the next point snaps to, from `smartSnap` (the same answer the click gets). */
const SNAP_TEXT: Partial<Record<SmartSnap["kind"], MessageKey>> = { corner: "sketch.snap.corner", midpoint: "sketch.snap.midpoint", "on-line": "sketch.snap.onLine", parallel: "sketch.snap.parallel", perpendicular: "sketch.snap.perpendicular", intersection: "sketch.snap.intersection", extension: "sketch.snap.extension" };

function SmartCue({ at, snap }: { at: Vec2; snap: SmartSnap }) {
  const tx = useSketchText();
  const key = SNAP_TEXT[snap.kind];
  const label = key ? tx.t(key) : snap.label;
  if (snap.kind === "free") return <circle cx={at[0]} cy={at[1]} r={4} className="sk-snap-dot" />;
  return (
    <g>
      {snap.kind === "corner" && <circle cx={at[0]} cy={at[1]} r={9} className="sk-snap-ring is-vertex" />}
      {snap.kind === "midpoint" && <path d={`M${at[0] - 8},${at[1] + 6} L${at[0]},${at[1] - 8} L${at[0] + 8},${at[1] + 6} Z`} className="sk-snap-ring is-mid" />}
      {(snap.kind === "on-line" || snap.kind === "intersection") && <rect x={at[0] - 6} y={at[1] - 6} width={12} height={12} transform={`rotate(45 ${at[0]} ${at[1]})`} className="sk-snap-ring is-edge" />}
      <circle cx={at[0]} cy={at[1]} r={4} className="sk-snap-dot" />
      {label && <Cue at={at} label={label} kind={SNAP_CUE[snap.kind] ?? "square"} />}
    </g>
  );
}

/** The selected footprint side's chip: "Hip ⇄ Gable", click to flip. */
function SideChip({ at, side, onToggle }: { at: Vec2; side: { mode: "hip" | "gable"; allowed: boolean }; onToggle: () => void }) {
  const tx = useSketchText();
  const label = tx.t(side.mode === "hip" ? "sketch.canvas.hipMakeGable" : "sketch.canvas.gableMakeHip");
  const text = side.allowed ? label : tx.t("sketch.canvas.insideCorner");
  const textW = labelWidth(text, 6.3);
  const w = textW + 42;
  return (
    <g
      className={`sk-side-chip${side.allowed ? "" : " is-disabled"}`}
      transform={`translate(${at[0]},${at[1] - 30})`}
      onPointerDown={(e) => {
        e.stopPropagation();
        if (side.allowed) onToggle();
      }}
      role="button"
      aria-label={label}
    >
      <rect x={-w / 2} y={-13} width={w} height={26} rx={13} />
      <path transform={`translate(${-w / 2 + 16},0)`} d={side.mode === "hip" ? "M-7,5 L-3,-4 H3 L7,5 Z" : "M-7,5 V0 L0,-6 L7,0 V5 Z"} className="sk-side-chip-icon" />
      <text x={-w / 2 + 29 + textW / 2}>{text}</text>
    </g>
  );
}

/** The modules' colours from the sketch.css tokens (light or dark theme), and the conflict hatch. */
function panelLook(c: HTMLCanvasElement, g: CanvasRenderingContext2D): PanelLook {
  const css = getComputedStyle(c);
  const v = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
  return {
    fill: v("--sk-panel-fill", "#15214a"),
    edge: v("--sk-panel-edge", "rgba(226, 232, 255, 0.85)"),
    cell: v("--sk-panel-cell", "rgba(160, 182, 255, 0.28)"),
    selectedFill: v("--sk-panel-fill-selected", "#27261f"),
    selected: v("--sk-selected", "#ffd02e"),
    conflict: v("--sk-conflict", "#ff4d5e"),
    conflictFill: conflictHatch(g),
    heat: heatColor,
  };
}

let hatch: { ctx: CanvasRenderingContext2D; pattern: CanvasPattern | string } | null = null;
/** Red and dark-red stripes at 45° (the SVG's `sk-conflict-hatch`), made once per canvas. */
function conflictHatch(g: CanvasRenderingContext2D): CanvasPattern | string {
  if (hatch?.ctx === g) return hatch.pattern;
  const t = document.createElement("canvas");
  t.width = t.height = 8;
  const q = t.getContext("2d");
  let pattern: CanvasPattern | string = "rgba(255, 77, 94, 0.6)";
  if (q) {
    q.fillStyle = "rgba(120, 10, 25, 0.85)";
    q.fillRect(0, 0, 8, 8);
    q.strokeStyle = "rgba(255, 77, 94, 0.9)";
    q.lineWidth = 2.8;
    q.beginPath();
    for (const o of [-8, 0, 8]) {
      q.moveTo(o, 8);
      q.lineTo(o + 8, 0);
    }
    q.stroke();
    pattern = g.createPattern(t, "repeat") ?? pattern;
  }
  hatch = { ctx: g, pattern };
  return pattern;
}
